import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, MapPin, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useSearchPlacesForLinking } from "@/hooks/useSearchPlacesForLinking";
import { usePlaceLinkingLabel } from "@/hooks/usePlaceLinkingLabel";
import { useSetProducerPlace } from "@/hooks/useProducerPlace";
import { Skeleton } from "@/components/ui/skeleton";

interface PlaceSelectorProps {
  producerId: string;
  currentPlaceId: string | null;
  currentPlaceName?: string | null;
  disabled?: boolean;
}

export function PlaceSelector({
  producerId,
  currentPlaceId,
  disabled = false,
}: PlaceSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: places, isLoading } = useSearchPlacesForLinking(searchQuery);
  const { data: linkedPlace } = usePlaceLinkingLabel(currentPlaceId);
  const setPlaceMutation = useSetProducerPlace();

  const handleSelect = (placeId: string) => {
    if (placeId === currentPlaceId) {
      setOpen(false);
      return;
    }
    setPlaceMutation.mutate({ producerId, placeId });
    setOpen(false);
  };

  const handleClear = () => {
    setPlaceMutation.mutate({ producerId, placeId: null });
  };

  // Use linkedPlace from dedicated RPC as fallback when search list doesn't contain it
  const displayName = useMemo(() => {
    const fromList = places?.find((p) => p.id === currentPlaceId);
    if (fromList) return fromList.name;
    if (linkedPlace) return linkedPlace.name;
    return null;
  }, [places, currentPlaceId, linkedPlace]);

  const displaySub = useMemo(() => {
    const fromList = places?.find((p) => p.id === currentPlaceId);
    const src = fromList ?? linkedPlace;
    if (!src) return null;
    return [src.municipality, src.landskap].filter(Boolean).join(", ");
  }, [places, currentPlaceId, linkedPlace]);

  return (
    <div className="space-y-2">
      {/* Current link status */}
      {currentPlaceId && displayName && (
        <p className="text-sm text-muted-foreground">
          Kopplad till: <span className="font-medium text-foreground">{displayName}</span>
          {displaySub && <span className="ml-1">({displaySub})</span>}
        </p>
      )}
      {!currentPlaceId && (
        <p className="text-sm text-muted-foreground">Inte kopplad än</p>
      )}

      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
              disabled={disabled || setPlaceMutation.isPending}
            >
              <span className="flex items-center gap-2 truncate">
                <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                {displayName || "Välj en plats…"}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Sök plats…"
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                {isLoading ? (
                  <div className="p-2 space-y-2">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : (
                  <>
                    {(!places || places.length === 0) && (
                      <CommandEmpty>
                        <div className="py-6 text-center text-sm">
                          {searchQuery.trim() ? (
                            <p className="text-muted-foreground">Ingen plats hittades.</p>
                          ) : (
                            <>
                              <p className="text-muted-foreground">Inga platser att välja just nu.</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Saknas din plats? Skicka ett platsförslag!
                              </p>
                            </>
                          )}
                        </div>
                      </CommandEmpty>
                    )}
                    <CommandGroup>
                      {(places ?? []).map((place) => (
                        <CommandItem
                          key={place.id}
                          value={place.id}
                          onSelect={() => handleSelect(place.id)}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              currentPlaceId === place.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div className="flex flex-col">
                            <span className="font-medium">{place.name}</span>
                            {place.municipality && (
                              <span className="text-xs text-muted-foreground">
                                {place.municipality}
                                {place.landskap && `, ${place.landskap}`}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {currentPlaceId && !disabled && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClear}
            disabled={setPlaceMutation.isPending}
            className="shrink-0"
            title="Ta bort platskoppling"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
